import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'
import PageHeader from '../../common/PageHeader'
import Section, { SubSection } from '../../common/Section'
import Alert from '../../common/Alert'
import CodeBlock, { SimpleCodeBlock, InlineCode } from '../../common/CodeBlock'
import { ParamTable } from '../../common/Table'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import PrintIcon from '@mui/icons-material/Print'
import { useLocale } from '../../../i18n/useLocale'

export default function LabelGeneration() {
  const { urls, brand } = useLocale()

  return (
    <Box>
      <PageHeader
        title="Label Generation"
        description="Generate shipping labels in PDF or ZPL format for your parcels."
        breadcrumbs={[
          { label: 'Guides', path: '/guides' },
          { label: 'Label Generation' },
        ]}
      />

      <Section title="Label Formats" divider={false}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {brand.name} supports two label formats to accommodate different printing requirements:
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PictureAsPdfIcon sx={{ color: 'error.main', fontSize: 28 }} />
                  <Typography variant="h6">PDF Labels</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Standard PDF format suitable for office printers. Returns a single PDF document
                  with one A6-sized label per page.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label="A6 Size" size="small" variant="outlined" />
                  <Chip label="105 × 148 mm" size="small" variant="outlined" />
                  <Chip label="Batch Support" size="small" variant="outlined" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PrintIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                  <Typography variant="h6">ZPL Labels</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Zebra Programming Language format for thermal label printers. Returns Base64-encoded
                  ZPL content for each parcel.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label="203 DPI" size="small" variant="outlined" />
                  <Chip label="Thermal Printers" size="small" variant="outlined" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Section>

      <Section title="Generate PDF Labels">
        <SimpleCodeBlock code="POST /labels/pdf" />

        <SubSection title="Request">
          <Typography variant="body2" sx={{ mb: 2 }}>
            Send an array of parcel numbers to generate labels:
          </Typography>
          <CodeBlock
            title="Generate PDF Labels"
            examples={[
              {
                language: 'bash',
                label: 'cURL',
                code: `curl -X POST "${urls.apiBase}/labels/pdf" \\
  -H "Content-Type: application/json" \\
  -H "X-API-KEY: your-api-key" \\
  -d '["Q17563175099981697", "Q17564837305001818"]' \\
  --output labels.pdf`,
              },
              {
                language: 'javascript',
                label: 'Node.js',
                code: `const parcelNumbers = ['Q17563175099981697', 'Q17564837305001818'];

const response = await fetch('${urls.apiBase}/labels/pdf', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-KEY': process.env.API_KEY,
  },
  body: JSON.stringify(parcelNumbers)
});

// Save the PDF
const buffer = await response.arrayBuffer();
fs.writeFileSync('labels.pdf', Buffer.from(buffer));`,
              },
              {
                language: 'python',
                label: 'Python',
                code: `import requests

parcel_numbers = ['Q17563175099981697', 'Q17564837305001818']

response = requests.post(
    '${urls.apiBase}/labels/pdf',
    json=parcel_numbers,
    headers={
        'Content-Type': 'application/json',
        'X-API-KEY': os.environ['API_KEY'],
    }
)

# Save the PDF
with open('labels.pdf', 'wb') as f:
    f.write(response.content)`,
              },
            ]}
          />
        </SubSection>

        <SubSection title="Response">
          <Typography variant="body2">
            Returns a binary PDF file with <InlineCode>Content-Type: application/pdf</InlineCode>.
            The PDF contains one A6-sized page per parcel.
          </Typography>
        </SubSection>
      </Section>

      <Section title="Generate ZPL Labels">
        <SimpleCodeBlock code="POST /labels/zpl" />

        <SubSection title="Query Parameters">
          <ParamTable
            params={[
              {
                name: 'dpi',
                type: 'string',
                required: false,
                description: 'Reserved for future use — currently ignored; output is produced at the platform default density.',
              },
            ]}
          />
        </SubSection>

        <SubSection title="Request">
          <CodeBlock
            title="Generate ZPL Labels"
            examples={[
              {
                language: 'bash',
                label: 'cURL',
                code: `curl -X POST "${urls.apiBase}/labels/zpl" \\
  -H "Content-Type: application/json" \\
  -H "X-API-KEY: your-api-key" \\
  -d '["Q17563175099981697"]'`,
              },
              {
                language: 'javascript',
                label: 'Node.js',
                code: `const parcelNumbers = ['Q17563175099981697'];

const response = await fetch('${urls.apiBase}/labels/zpl', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-KEY': process.env.API_KEY,
  },
  body: JSON.stringify(parcelNumbers)
});

const labels = await response.json();

// Each label contains Base64-encoded ZPL
labels.forEach(label => {
  const zplContent = Buffer.from(label.zplContent, 'base64').toString('utf8');
  console.log('Parcel:', label.parcelNumber);
  // Send to printer...
});`,
              },
            ]}
          />
        </SubSection>

        <SubSection title="Response">
          <SimpleCodeBlock
            code={`[
  {
    "parcelNumber": "Q17563175099981697",
    "zplContent": "Xl5YQV5eTk5eUE9SMCU..."  // Base64-encoded ZPL
  },
  {
    "parcelNumber": "Q17564837305001818",
    "zplContent": "Xl5YQV5eTk5eUE9SMCU..."
  }
]`}
          />
          <Alert type="info">
            The <InlineCode>zplContent</InlineCode> is Base64-encoded. Decode it before sending to the printer.
          </Alert>
        </SubSection>
      </Section>

      <Section title="Label Contents">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Each generated label includes:
        </Typography>

        <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
          <li><strong>Company Logo</strong> - {brand.name} branding (top-left)</li>
          <li><strong>QR Codes</strong> - Two QR codes for scanning (top-right and bottom-left)</li>
          <li><strong>Parcel Number</strong> - Unique identifier with Code128 barcode</li>
          <li><strong>Sender Information</strong> - Name and phone number</li>
          <li><strong>Recipient Information</strong> - Name and phone number</li>
          <li><strong>Destination APM</strong> - Pickup location name and address</li>
          <li><strong>Reference Code</strong> - Your order reference</li>
          <li><strong>Routing Information</strong> - Depot, route, and box codes</li>
          <li><strong>Print Timestamp</strong> - When the label was generated</li>
        </Box>

        <Card variant="outlined" sx={{ mt: 3, p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            [Label preview would be shown here in production]
          </Typography>
        </Card>
      </Section>

      <Section title="Printing Best Practices">
        <SubSection title="PDF Labels">
          <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
            <li>Print at 100% scale (no scaling/fit-to-page)</li>
            <li>Use A6 paper or cut A4 sheets to size</li>
            <li>Recommended: Label stickers for easy application</li>
          </Box>
        </SubSection>

        <SubSection title="ZPL Labels">
          <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
            <li>Output targets standard 203 DPI Zebra printers</li>
            <li>Decode Base64 content before sending to printer</li>
          </Box>
        </SubSection>

        <Alert type="warning" title="Barcode Quality">
          Ensure labels are printed clearly with no smudging. Scanners at APMs require readable barcodes
          to process parcels.
        </Alert>
      </Section>

      <Section title="Return Labels">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Return parcels have dedicated endpoints (the parcel must be an actual return — i.e. have a
          return type — otherwise the request fails with <InlineCode>WRONG_PARCEL_TYPE</InlineCode>):
        </Typography>
        <SimpleCodeBlock
          code={`POST /labels/pdf/return
POST /labels/zpl/return`}
        />
      </Section>

      <Section title="Preconditions & Error Handling">
        <Typography variant="body1" sx={{ mb: 2 }}>
          A label is only issued when the parcel exists, belongs to your account, is not
          deleted/blocked/expired, and — for pre-paid accounts — the sending fee is settled
          (post-paid accounts always pass the payment check). All failures return{' '}
          <strong>HTTP 400</strong> (there is no 404):
        </Typography>

        <SimpleCodeBlock
          code={`{
  "timestamp": "2026-01-15 14:30:00",
  "message": "Parcel is not payed with number: Q99999999999999999",
  "status": 400
}`}
        />

        <Alert type="info">
          Labels can be requested by {brand.name} parcel number, by your own{' '}
          <InlineCode>uniqueParcelNumber</InlineCode>, or by external parcel number. For batch
          requests, if any parcel fails a precondition, the entire request fails — validate parcel
          numbers before requesting labels.
        </Alert>
      </Section>
    </Box>
  )
}
